import React from "react";
import { Box, Typography, Avatar, Paper, Chip, useTheme } from "@mui/material";
import {
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
} from "@mui/icons-material";
import { tokens } from "../../../theme";

interface OrganizerCardProps {
  id: string;
  organizer: {
    name: string;
    relation: string;
    email: string;
    phone: string;
    address: string;
    contact_image?: string;
    specialNotes: string;
    notes: string;
  };
  serviceType?: string;
}

const OrganizerCard: React.FC<OrganizerCardProps> = ({
  id,
  organizer,
  serviceType,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const MAX_NOTES_LENGTH = 70;
  const [showFullNotes, setShowFullNotes] = React.useState(false);
  const notes = organizer.notes ?? "";
  const isNotesLong = notes.length > MAX_NOTES_LENGTH;
  const notesDisplayText =
    isNotesLong && !showFullNotes
      ? notes.substring(0, MAX_NOTES_LENGTH) + "..."
      : notes;

  return (
    <Paper sx={{ p: 2, height: "100%", boxShadow: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: "medium" }}>
          Client's Information
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            label={`ID:#${id}`}
            size="small"
            sx={{
              bgcolor: colors.neutral[700],
              color: colors.neutral[600],
              fontWeight: "medium",
              borderRadius: "8px",
            }}
          />
          {serviceType && (
            <Chip
              label={serviceType}
              size="small"
              sx={{
                bgcolor: colors.neutral[700],
                color: colors.neutral[600],
                fontWeight: "medium",
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
      </Box>

      <Box mt={2} display="flex" alignItems="center">
        <Avatar
          src={organizer.contact_image}
          sx={{ width: 56, height: 56, mr: 2 }}
        />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {organizer.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.neutral[600], fontSize: 14 }}
          >
            {organizer.relation}
          </Typography>
        </Box>
      </Box>

      <Box mt={3} display="flex" alignItems="center">
        <EmailOutlined
          sx={{
            mr: 1,
            color: colors.neutral[600],
            fontSize: 20,
          }}
        />
        <Typography
          variant="body1"
          sx={{ fontSize: 15, color: colors.neutral[600] }}
        >
          {organizer.email}
        </Typography>
      </Box>

      <Box mt={1.5} display="flex" alignItems="center">
        <PhoneOutlined
          sx={{
            mr: 1,
            color: colors.neutral[600],
            fontSize: 20,
          }}
        />
        <Typography
          variant="body1"
          sx={{ fontSize: 15, color: colors.neutral[600] }}
        >
          {organizer.phone}
        </Typography>
      </Box>

      <Box mt={1.5} display="flex" alignItems="center">
        <LocationOnOutlined
          sx={{
            mr: 1,
            color: colors.neutral[600],
            fontSize: 20,
          }}
        />
        <Typography
          variant="body1"
          sx={{ fontSize: 15, color: colors.neutral[600] }}
        >
          {organizer.address}
        </Typography>
      </Box>

      <Box mt={3}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: 14, fontWeight: "medium", mb: 1 }}
        >
          Special Request
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: "8px",
            bgcolor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: 14, color: colors.neutral[600] }}
          >
            {organizer.specialNotes}
          </Typography>
        </Paper>
      </Box>

      <Box mt={2}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: 14, fontWeight: "medium", mb: 1 }}
        >
          Notes
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: "8px",
            bgcolor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: 14, color: colors.neutral[600] }}
          >
            {notesDisplayText}
            {isNotesLong && (
              <Typography
                component="span"
                sx={{
                  color: colors.primary[500],
                  fontWeight: "medium",
                  cursor: "pointer",
                  ml: 0.5,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={() => setShowFullNotes(!showFullNotes)}
              >
                {showFullNotes ? " show less" : " view more"}
              </Typography>
            )}
          </Typography>
        </Paper>
      </Box>
    </Paper>
  );
};

export default OrganizerCard;
